import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FileTextIcon } from "lucide-react";
import { useMemo } from "react";

import { ErrorPage } from "@/components/error-page";
import { Engine, type EngineAttemptSnapshot } from "@/components/studyos";
import { specToStudySession } from "@/components/studyos/spec-to-session";
import { Empty, EmptyDescription, EmptyMedia } from "@/components/ui/empty";
import { versionAttemptQueryOptions, versionQueryOptions } from "@/hooks/query/thread";
import { useSession } from "@/lib/auth-client";
import { createWebPageJsonLd, noIndexSeo, seo } from "@/lib/seo";
import * as m from "@/paraglide/messages";
import { $checkBlockAction } from "@/server/actions/block-action";
import { $joinSharedVersion } from "@/server/actions/content";

import { ShareAccessDialog } from "../-components/share-access-dialog";
import { useSaveAttempt } from "../-hooks/use-save-attempt";

export const Route = createFileRoute("/learn/$versionId/")({
	component: RouteComponent,
	loader: async ({ context }) => {
		return {
			versionTitle: context.versionTitle,
			visibility: context.visibility,
			status: context.status,
		};
	},
	head: ({ loaderData, match }) => {
		const title = loaderData?.versionTitle
			? m.learn_pageTitle({ versionTitle: loaderData.versionTitle })
			: m.learn_defaultTitle();
		const description = m.learn_description();

		if (loaderData?.visibility !== "public" || loaderData?.status !== "ok") {
			return noIndexSeo({
				title,
				description,
				pathname: match.pathname,
			});
		}

		return seo({
			title,
			description,
			pathname: match.pathname,
			structuredData: createWebPageJsonLd({
				title,
				description,
				pathname: match.pathname,
			}),
		});
	},
	errorComponent: ErrorPage,
});

function RouteComponent() {
	const { versionId } = Route.useParams();
	const access = useSuspenseQuery(versionQueryOptions({ versionId })).data;

	if (access.status !== "ok") {
		return <AccessGuard versionId={versionId} access={access} />;
	}

	return <LearnContent versionId={versionId} access={access} />;
}

function AccessGuard({
	versionId,
	access,
}: {
	versionId: string;
	access: {
		status: string;
		share?: {
			organization?: { name: string } | null;
			team?: { name: string } | null;
		} | null;
	};
}) {
	const queryClient = useQueryClient();
	const joinMutation = useMutation({
		mutationKey: ["versions", "join", versionId],
		mutationFn: () => $joinSharedVersion({ data: { versionId } }),
		onSuccess: async () => {
			queryClient.invalidateQueries({
				queryKey: ["version-attempts", versionId],
			});
		},
	});

	return (
		<div className="flex min-h-full items-center justify-center bg-muted/20 px-4">
			<ShareAccessDialog
				versionId={versionId}
				status={access.status}
				organizationName={access.share?.organization?.name ?? null}
				teamName={access.share?.team?.name ?? null}
				isJoining={joinMutation.isPending}
				onJoin={() => joinMutation.mutate()}
			/>
		</div>
	);
}

function LearnContent({
	versionId,
	access,
}: {
	versionId: string;
	access: {
		status: string;
		version?: { output?: { type: string; ui?: unknown } | null } | null;
	};
}) {
	const { data: sessionResponse, isPending: isSessionPending } = useSession();
	const shouldTrackAttempt = !!sessionResponse?.session;
	const attemptQuery = useQuery(
		versionAttemptQueryOptions({
			versionId,
			enabled: shouldTrackAttempt,
		}),
	);
	const activeAttempt = attemptQuery.data ?? null;

	const { queueCheckpoint, completionMutation } = useSaveAttempt({
		versionId,
		attemptId: activeAttempt?.id ?? null,
	});

	const session = useMemo(() => {
		if (access.version?.output?.type !== "ui" || !access.version.output.ui) {
			return null;
		}

		return specToStudySession(access.version.output.ui, {
			mode: "study",
			versionId: versionId,
		});
	}, [access, versionId]);

	console.log(access.version.output.ui, session);

	if (isSessionPending || (shouldTrackAttempt && attemptQuery.isPending)) {
		return <div className="p-6 text-sm text-muted-foreground">{m.learn_loadingAttempt()}</div>;
	}

	if (!session || session.blocks.length === 0) {
		return (
			<div className="flex flex-1 items-center justify-center p-6">
				<Empty>
					<EmptyMedia variant="icon">
						<FileTextIcon />
					</EmptyMedia>
					<EmptyDescription>{m.learn_noContent()}</EmptyDescription>
				</Empty>
			</div>
		);
	}

	return (
		<Engine
			key={versionId}
			className="min-h-0 min-w-0 flex-1"
			session={session}
			initialAttemptSnapshot={
				(activeAttempt?.engineState as EngineAttemptSnapshot | undefined) ?? undefined
			}
			onBlockAction={async (blockId, state) => {
				return $checkBlockAction({
					data: { versionId, blockId, state },
				});
			}}
			onCheckpoint={(snapshot) => {
				void queueCheckpoint(snapshot);
			}}
			onComplete={(snapshot) => {
				if (!activeAttempt?.id || completionMutation.isPending) {
					return;
				}

				completionMutation.mutate(snapshot);
			}}
		/>
	);
}
